import type { CookingTechnique } from '@familychef/shared';
import {
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  TECHNIQUE_CATEGORIES,
  TECHNIQUE_CATEGORY_LABELS,
} from '@familychef/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  name: z.string().min(1, 'Requis'),
  description: z.string().nullable().optional(),
  category: z.enum(['cutting', 'cooking', 'preparation', 'baking', 'preservation', 'other']),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  estimated_duration: z.number().int().positive().nullable().optional(),
  steps: z.array(z.string()),
  tips: z.array(z.string()),
  image_url: z.string().url().nullable().optional().or(z.literal('')),
  video_url: z.string().url().nullable().optional().or(z.literal('')),
});

type FormData = z.infer<typeof formSchema>;

interface TechniqueFormProps {
  defaultValues?: Partial<CookingTechnique>;
  onSubmit: (data: FormData) => Promise<void>;
  isLoading?: boolean;
  onCancel: () => void;
  mode: 'create' | 'edit';
}

export function TechniqueForm({
  defaultValues,
  onSubmit,
  isLoading,
  onCancel,
  mode,
}: TechniqueFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name ?? '',
      description: defaultValues?.description ?? '',
      category: defaultValues?.category ?? 'other',
      difficulty: defaultValues?.difficulty ?? 'easy',
      estimated_duration: defaultValues?.estimated_duration ?? undefined,
      steps: defaultValues?.steps ?? [''],
      tips: defaultValues?.tips ?? [],
      image_url: defaultValues?.image_url ?? '',
      video_url: defaultValues?.video_url ?? '',
    },
  });

  const watchedSteps = watch('steps');
  const watchedTips = watch('tips');

  const addStep = useCallback(
    () => setValue('steps', [...(watchedSteps ?? []), '']),
    [watchedSteps, setValue],
  );
  const updateStep = useCallback(
    (i: number, v: string) => {
      const next = [...(watchedSteps ?? [])];
      next[i] = v;
      setValue('steps', next);
    },
    [watchedSteps, setValue],
  );
  const removeStep = useCallback(
    (i: number) =>
      setValue(
        'steps',
        (watchedSteps ?? []).filter((_, idx) => idx !== i),
      ),
    [watchedSteps, setValue],
  );

  const addTip = useCallback(
    () => setValue('tips', [...(watchedTips ?? []), '']),
    [watchedTips, setValue],
  );
  const updateTip = useCallback(
    (i: number, v: string) => {
      const next = [...(watchedTips ?? [])];
      next[i] = v;
      setValue('tips', next);
    },
    [watchedTips, setValue],
  );
  const removeTip = useCallback(
    (i: number) =>
      setValue(
        'tips',
        (watchedTips ?? []).filter((_, idx) => idx !== i),
      ),
    [watchedTips, setValue],
  );

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-6">
      <div className="card">
        <h3 className="mb-4 font-semibold text-slate-900">Informations générales</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Nom *</label>
            <input
              {...register('name')}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Ex: Brunoise"
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Catégorie *</label>
            <select
              {...register('category')}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
            >
              {TECHNIQUE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {TECHNIQUE_CATEGORY_LABELS[c]?.fr}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Difficulté *</label>
            <select
              {...register('difficulty')}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {DIFFICULTY_LABELS[d]?.fr}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Durée estimée (min)
            </label>
            <input
              type="number"
              {...register('estimated_duration', { valueAsNumber: true })}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Ex: 5"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Description de la technique..."
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">URL image</label>
            <input
              {...register('image_url')}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">URL vidéo</label>
            <input
              {...register('video_url')}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      {/* Étapes */}
      <div className="card">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Étapes ({(watchedSteps ?? []).length})</h3>
          <button
            type="button"
            onClick={addStep}
            className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
          >
            <Plus size={14} /> Ajouter une étape
          </button>
        </div>
        <div className="space-y-2">
          {(watchedSteps ?? []).map((step, i) => (
            <div key={i} className="flex gap-2">
              <span className="flex h-9 w-7 shrink-0 items-center justify-center text-xs font-bold text-slate-400">
                {i + 1}
              </span>
              <input
                value={step}
                onChange={(e) => updateStep(i, e.target.value)}
                className="h-9 flex-1 rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
                placeholder={`Étape ${i + 1}...`}
              />
              <button
                type="button"
                onClick={() => removeStep(i)}
                className="text-red-400 hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Conseils */}
      <div className="card">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Conseils</h3>
          <button
            type="button"
            onClick={addTip}
            className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
          >
            <Plus size={14} /> Ajouter
          </button>
        </div>
        <div className="space-y-2">
          {(watchedTips ?? []).map((tip, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={tip}
                onChange={(e) => updateTip(i, e.target.value)}
                className="h-9 flex-1 rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
                placeholder={`Conseil ${i + 1}...`}
              />
              <button
                type="button"
                onClick={() => removeTip(i)}
                className="text-red-400 hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {(watchedTips ?? []).length === 0 && (
            <p className="text-sm text-slate-400">Aucun conseil ajouté</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pb-8">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-60"
        >
          {isLoading
            ? 'Enregistrement...'
            : mode === 'create'
              ? 'Créer la technique'
              : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}
